import abacusLogoDark from "../../assets/logo/abacus-dark.svg"
import abacusLogo from "../../assets/logo/abacus.svg"
import chevronLogo from "../../assets/logo/chevron.svg"
import honestTile from "../../assets/logo/honest.svg"
import { useTheme } from "../../theme/useTheme"
import { ContentSection } from "../layout/ContentSection"
import { Emphasize, SectionItemTitle, Skill } from "../ui/CustomTypography"
import { Link } from "../ui/Link"
import { LinkIcon } from "../ui/LinkIcon"
import { TimeLocationContainer } from "../ui/TimeLocationContainer"
import { Box, Typography } from "@mui/material"
import React from "react"

const MARKOV_CHAIN_ARTICLE_URL = "https://wisl.dev/blog/markov-chain-risk-portfolio/"
const PRODUCTION_OPTIMIZATION_URL = "https://wisl.dev/blog/lp-oil-production-optimization/"
const PIPELINE_NETWORK_MODELING_URL = "https://wisl.dev/blog/pipeline-network-modeling-tool/"
const ML_FRAMEWORK_URL = "https://wisl.dev/blog/monadic-ml-framework/"
const CICD_PIPELINE_URL = "https://wisl.dev/blog/ci-cd-dev-to-prod/"
const API_GATEWAY_URL = "https://wisl.dev/blog/resilient-api-gateway/"
// the "task runner" anchor in the packages bullet links the tool's docs
// directly; the project name lives in the Open Source section only
const TASK_RUNNER_URL = "https://bakefile.wisl.dev"

// grow inline links to the 44px touch floor with padding + cancelling
// negative margin — deliberately NOT inline-block: these links wrap across
// lines, and inline-block would freeze each fragment into an unbreakable
// block that overflows narrow sheets
const inlineLinkSx = {
    padding: "10px 2px",
    margin: "-10px -2px",
}

interface Experience {
    logo: string
    companyName: string
    companyDescription: string | React.ReactNode
    positionName: string
    positionExtra?: React.ReactNode
    location: string
    startTime: Date
    endTime: Date | "present"
    jobDescriptions: React.ReactNode[]
}

interface ExperienceProps {
    startExperience?: number
    endExperience?: number
    startJobDescription?: number
    endJobDescription?: number
}

export const Experience = ({
    startExperience = 0,
    endExperience,
    startJobDescription = 0,
    endJobDescription,
}: ExperienceProps = {}) => {
    const { layout, breakpoints, themeSettings, audience } = useTheme()

    const formatDate = (date: Date) =>
        date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    const experiences: Experience[] = [
        {
            logo: themeSettings.isDarkMode ? abacusLogoDark : abacusLogo,
            companyName: "ABACUS digital",
            companyDescription: (
                <>
                    fintech, SCB subsidiary
                    {audience.mode !== "thai" ? ", Thailand's largest bank" : ""}
                </>
            ),
            positionName: "Senior Data Engineer",
            location: "Bangkok, Thailand",
            startTime: new Date("2024-07-01"),
            endTime: "present",
            jobDescriptions: [
                <>
                    Built the <Skill>GCP</Skill> landing zone solo, as code: <Skill>Terraform</Skill>/
                    <Skill>Terragrunt</Skill> covering network, <Skill>IAM</Skill>, security, and
                    governance. The data platform (<Skill>BigQuery</Skill>, <Skill>Dataflow</Skill>,{" "}
                    <Skill>Airflow</Skill>) and its microservices run on top of it.
                </>,
                <>
                    Own data platforms, <Skill>ETL/ELT</Skill> pipelines, warehousing, and microservices
                    in <Skill>Python</Skill>, <Skill>Go</Skill>, and <Skill>SQL</Skill> on{" "}
                    <Skill>GCP</Skill> (<Skill>Pub/Sub</Skill>, <Skill>Cloud Run</Skill>,{" "}
                    <Skill>Cloud SQL</Skill>, <Skill>Firestore</Skill>, <Skill>Cloud Tasks</Skill>,{" "}
                    <Skill>Cloud Storage</Skill>), with <Skill>AWS</Skill> integrations.
                </>,
                <>
                    Built <Emphasize>real-time services</Emphasize> (<Skill>FastAPI</Skill>,{" "}
                    <Skill>asynchronous</Skill> processing, <Emphasize>40 req/s peak</Emphasize>) as the{" "}
                    <Skill>Elasticsearch</Skill> gateway,{" "}
                    <Link href={API_GATEWAY_URL} sx={inlineLinkSx}>
                        cutting downtime by <Emphasize>~70%</Emphasize>
                        <LinkIcon />
                    </Link>
                    ; built a <Skill>Dataflow</Skill> real-time analytics service (
                    <Emphasize>1-2 min freshness</Emphasize>) supporting{" "}
                    <Emphasize>10-minute loan approval</Emphasize>.
                </>,
                <>
                    Built <Emphasize>internal Python packages to standardize and automate</Emphasize>{" "}
                    recurring engineering work:{" "}
                    <Link href={TASK_RUNNER_URL} sx={inlineLinkSx}>
                        a task runner
                        <LinkIcon />
                    </Link>
                    , reusable <Skill>CI/CD</Skill> pipelines, versioning automation,{" "}
                    <Skill>Dataflow</Skill> abstractions, <Skill>LangChain</Skill>/<Skill>LLM</Skill>{" "}
                    interfaces, and an LLM data-discovery prototype (<Skill>embeddings</Skill>,{" "}
                    <Skill>vector search</Skill>) for natural-language queries.
                </>,
                <>
                    Applied{" "}
                    <Link href={CICD_PIPELINE_URL} sx={inlineLinkSx}>
                        <Skill>CI/CD</Skill>
                        <LinkIcon />
                    </Link>{" "}
                    and <Skill>IaC</Skill> (<Skill>GitHub Actions</Skill>, <Skill>Terraform</Skill>,{" "}
                    <Skill>Docker</Skill>) across data and AI/ML projects:{" "}
                    <Emphasize>daily deploys</Emphasize>, <Emphasize>all four DORA metrics up</Emphasize>
                    . Mentor engineers and interns; drive <Skill>code reviews</Skill>,{" "}
                    <Skill>system design</Skill>, and <Skill>test-driven development</Skill>.
                </>,
            ],
        },
        {
            // single tile for both themes (matches the portfolio's honest.svg)
            logo: honestTile,
            companyName: "Honest Technology",
            companyDescription: "fintech",
            positionName: "Senior Data Scientist",
            location: "Bangkok, Thailand",
            startTime: new Date("2022-07-01"),
            endTime: new Date("2024-03-07"),
            jobDescriptions: [
                <>
                    Owned the production <Skill>ML</Skill> lifecycle (<Skill>MLOps</Skill>) and{" "}
                    <Skill>hiring</Skill>. Introduced{" "}
                    <Link href={MARKOV_CHAIN_ARTICLE_URL} sx={inlineLinkSx}>
                        <Skill>Markov chain</Skill> labeling for the credit <Skill>risk model</Skill>
                        <LinkIcon />
                    </Link>
                    : <Emphasize>AUC up 7%</Emphasize>, contributing to a{" "}
                    <Emphasize>$20 million funding</Emphasize> round.
                </>,
                <>
                    Built{" "}
                    <Link href={ML_FRAMEWORK_URL} sx={inlineLinkSx}>
                        a monadic <Skill>machine learning</Skill> framework in <Skill>Python</Skill>
                        <LinkIcon />
                    </Link>
                    : <Emphasize>development time halved</Emphasize>,{" "}
                    <Emphasize>zero critical incidents</Emphasize>.
                </>,
                <>
                    Kept the codebase secure and reliable: <Skill>IaC</Skill>, <Skill>CI/CD</Skill>,
                    testing, and security scanning with <Skill>Terraform Cloud</Skill>,{" "}
                    <Skill>GitHub Actions</Skill>, <Skill>SonarCloud</Skill>, and <Skill>Trivy</Skill>.
                </>,
            ],
        },
        {
            // two-tone mark holds on light and dark — one asset, no swap
            logo: chevronLogo,
            companyName: "Chevron",
            companyDescription: "oil & gas",
            positionName: "Production Engineer",
            positionExtra: "(software development)",
            location: "Bangkok, Thailand",
            startTime: new Date("2017-07-01"),
            endTime: new Date("2022-04-30"),
            jobDescriptions: [
                <>
                    Built <Skill>Python</Skill> software for production engineering:{" "}
                    <Link href={PRODUCTION_OPTIMIZATION_URL} sx={inlineLinkSx}>
                        <Skill>linear programming</Skill> raised oil production{" "}
                        <Emphasize>15.2%</Emphasize> (<Emphasize>$15.3 million/year</Emphasize>)
                        <LinkIcon />
                    </Link>
                    ; pipeline network modeling cut investment costs by{" "}
                    <Link href={PIPELINE_NETWORK_MODELING_URL} sx={inlineLinkSx}>
                        <Emphasize>$49.8 million</Emphasize>
                        <LinkIcon />
                    </Link>
                    . Coordinated cross-functional projects adding <Emphasize>$128.5 million</Emphasize>{" "}
                    in benefits, with <Emphasize>zero safety incidents</Emphasize>.
                </>,
            ],
        },
    ]

    const filteredExperiences =
        endExperience !== undefined
            ? experiences.slice(startExperience, endExperience + 1)
            : experiences.slice(startExperience)

    // a page's slice can start past the last job (e.g. all jobs fit on the
    // first page) — render nothing rather than an empty Section whose
    // marginBottom would pad the section below it
    if (filteredExperiences.length === 0) return null

    return (
        <ContentSection title="Experience" hideTitle={startExperience !== 0}>
            {filteredExperiences.map((exp, index) => {
                const isFirstExp = index === 0
                const isLastExp = index === filteredExperiences.length - 1

                let jobDescriptions = exp.jobDescriptions
                if (isFirstExp && startJobDescription > 0) {
                    jobDescriptions = jobDescriptions.slice(startJobDescription)
                }
                if (isLastExp && endJobDescription !== undefined) {
                    jobDescriptions = jobDescriptions.slice(0, endJobDescription + 1)
                }

                const showHeader = !(isFirstExp && startJobDescription > 0)

                return (
                    <Box
                        key={startExperience + index}
                        // extra separation between jobs — the resume's main
                        // reading breaks deserve more air than section
                        // internals
                        sx={{ mb: layout.verticalSpacing * 2 }}
                        data-testid="experience-item"
                    >
                        {showHeader && (
                            <Box
                                sx={{
                                    display: "flex",
                                    // wide layout centers the short header; small
                                    // screens stack three lines, so top-align the
                                    // logo with the position title
                                    alignItems: breakpoints.isFullPage ? "center" : "flex-start",
                                    mb: layout.verticalSpacing * 0.5,
                                }}
                                data-testid="experience-header"
                            >
                                <Box
                                    data-testid="logo-container"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mr: 1.25,
                                    }}
                                >
                                    <img
                                        src={exp.logo}
                                        alt="Company Logo"
                                        style={{ width: 36, height: 36 }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }} data-testid="header-content-container">
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: breakpoints.isFullPage ? "row" : "column",
                                            justifyContent: "space-between",
                                        }}
                                        data-testid="first-line"
                                    >
                                        <Box data-testid="position-container">
                                            {/* title and parenthetical are atomic;
                                                the space between is the only break
                                                point on narrow screens */}
                                            <SectionItemTitle>
                                                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                                                    {exp.positionName}
                                                </Box>
                                            </SectionItemTitle>
                                            {exp.positionExtra && (
                                                <>
                                                    {" "}
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ display: "inline", whiteSpace: "nowrap" }}
                                                    >
                                                        {exp.positionExtra}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        {breakpoints.isFullPage && (
                                            <TimeLocationContainer
                                                location={exp.location}
                                                startTime={exp.startTime}
                                                endTime={exp.endTime}
                                                formatDate={formatDate}
                                            />
                                        )}
                                    </Box>
                                    {/* company line: medium-weight name + "·"
                                        description (600 competed with the title
                                        for emphasis) */}
                                    <Box data-testid="second-line" sx={{ lineHeight: 1.5 }}>
                                        <Typography
                                            variant="subtitle1"
                                            // subtitle1 defaults to <h6> — would skip
                                            // h3→h6 and fail axe heading-order; this
                                            // line is a label, not a heading
                                            component="p"
                                            sx={{ display: "inline", fontWeight: 500, lineHeight: 1.5 }}
                                        >
                                            {exp.companyName}
                                        </Typography>
                                        <Typography variant="body1" sx={{ display: "inline" }}>
                                            {" · "}
                                            {exp.companyDescription}
                                        </Typography>
                                    </Box>
                                    {!breakpoints.isFullPage && (
                                        <TimeLocationContainer
                                            location={exp.location}
                                            startTime={exp.startTime}
                                            endTime={exp.endTime}
                                            formatDate={formatDate}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                        <Box data-testid="job-descriptions">
                            {jobDescriptions.map((jobDescription, i) => (
                                <Typography key={i} variant="body1">
                                    • {jobDescription}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                )
            })}
        </ContentSection>
    )
}
